import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { Tooltip } from 'primereact/tooltip';

export interface DataPoint {
  value: string;
  metric: string;
  concern: string;
  threshold: string;
}

export interface RiskAlertDetails {
  title: string;
  message: string;
  level: 'high' | 'medium' | 'low';
  data_points?: DataPoint[];
  affected_positions?: string[];
  extended_reasoning?: string;
  recommended_action?: string;
}

interface RiskAlertDetailsDialogProps {
  visible: boolean;
  onHide: () => void;
  alert: RiskAlertDetails | null;
  tickerToCompanyName?: Map<string, string>;
}

export const RiskAlertDetailsDialog: React.FC<RiskAlertDetailsDialogProps> = ({
  visible,
  onHide,
  alert,
  tickerToCompanyName,
}) => {
  if (!alert) return null;

  const dialogFooter = (
    <div className="flex justify-content-end align-items-center gap-2">
      <Button
        label="Close"
        icon="pi pi-times"
        onClick={onHide}
        severity="secondary"
      />
    </div>
  );

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'high':
        return 'var(--bq-color-negative)';
      case 'medium':
        return 'var(--bq-color-warning-strong)';
      default:
        return 'var(--bq-color-positive)';
    }
  };

  const getLevelSeverity = (level: string) => {
    switch (level) {
      case 'high':
        return 'danger';
      case 'medium':
        return 'warning';
      default:
        return 'success';
    }
  };

  // Helper function to normalize ticker and get company name
  const getCompanyName = (ticker: string): string | undefined => {
    if (!tickerToCompanyName || !ticker) return undefined;

    const normalizedTicker = ticker.toUpperCase().trim();

    // Try exact match first
    if (tickerToCompanyName.has(normalizedTicker)) {
      return tickerToCompanyName.get(normalizedTicker);
    }

    // Try base ticker (before colon, dot, or space)
    const baseTicker =
      normalizedTicker.split(':')[0] ||
      normalizedTicker.split('.')[0] ||
      normalizedTicker.split(' ')[0];
    if (baseTicker && baseTicker !== normalizedTicker) {
      if (tickerToCompanyName.has(baseTicker)) {
        return tickerToCompanyName.get(baseTicker);
      }
    }

    // Try alphanumeric-only version
    const alphanumeric = normalizedTicker.replace(/[^A-Z0-9]/g, '');
    if (alphanumeric && alphanumeric !== normalizedTicker) {
      if (tickerToCompanyName.has(alphanumeric)) {
        return tickerToCompanyName.get(alphanumeric);
      }
    }

    return undefined;
  };

  return (
    <Dialog
      header={
        <div className="flex align-items-center gap-3">
          <span>{alert.title}</span>
          <Tag
            value={alert.level.toUpperCase()}
            severity={getLevelSeverity(alert.level)}
            rounded
          />
        </div>
      }
      visible={visible}
      onHide={onHide}
      style={{ width: '80vw', maxWidth: '1000px' }}
      className="p-fluid"
      footer={dialogFooter}
      pt={{
        header: {
          style: {
            backgroundColor: 'var(--bq-surface-elevated)',
            color: 'var(--bq-text-primary)',
            padding: '1rem 1.5rem',
            minHeight: '60px',
            display: 'flex',
            alignItems: 'center',
          },
        },
        content: {
          style: {
            backgroundColor: 'var(--bq-surface-elevated)',
            color: 'var(--bq-text-primary)',
            maxHeight: '75vh',
            overflowY: 'auto',
            padding: '1.5rem',
          },
        },
        footer: {
          style: {
            backgroundColor: 'var(--bq-surface-elevated)',
            borderTop: '1px solid var(--bq-border-muted)',
            padding: '1rem 1.5rem',
            minHeight: '60px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          },
        },
      }}
    >
      <div className="flex flex-column gap-4">
        {/* Extended Reasoning */}
        {alert.extended_reasoning && (
          <div>
            <div className="flex align-items-center gap-2 mb-3">
              <i
                className="pi pi-file-edit"
                style={{
                  color: 'var(--bq-color-info)',
                  fontSize: '1.25rem',
                }}
              />
              <h4
                className="m-0"
                style={{ color: 'var(--bq-text-primary)', fontSize: '1.1rem' }}
              >
                Detailed Analysis
              </h4>
            </div>
            <Card
              className="grid-stack-item-content"
              pt={{
                root: {
                  style: {
                    backgroundColor: 'var(--bq-surface-body)',
                  },
                },
                body: { className: 'p-3 flex-1' },
                content: { className: 'p-0 m-0 flex-1' },
              }}
            >
              <p
                style={{
                  color: 'var(--bq-text-secondary)',
                  whiteSpace: 'pre-wrap',
                  margin: 0,
                  fontSize: '0.95rem',
                }}
              >
                {alert.extended_reasoning}
              </p>
            </Card>
          </div>
        )}

        {/* Data Points */}
        {alert.data_points && alert.data_points.length > 0 && (
          <div>
            <div className="flex align-items-center gap-2 mb-3">
              <i
                className="pi pi-chart-bar"
                style={{
                  color: 'var(--bq-color-info)',
                  fontSize: '1.25rem',
                }}
              />
              <h4
                className="m-0"
                style={{ color: 'var(--bq-text-primary)', fontSize: '1.1rem' }}
              >
                Key Metrics
              </h4>
            </div>
            <div className="grid">
              {alert.data_points.map((point, index) => (
                <div key={index} className="col-12 md:col-6">
                  <Card
                    className="grid-stack-item-content h-full flex flex-column"
                    pt={{
                      root: {
                        style: {
                          backgroundColor: 'var(--bq-surface-body)',
                        },
                      },
                      body: { className: 'p-3 flex-1' },
                      content: { className: 'p-0 m-0 flex-1' },
                    }}
                  >
                    <div className="flex flex-column h-full">
                      <div
                        className="font-semibold mb-2"
                        style={{
                          color: 'var(--bq-text-primary)',
                          fontSize: '0.95rem',
                        }}
                      >
                        {point.metric}
                      </div>
                      <div
                        className="text-xl font-bold mb-3"
                        style={{ color: getLevelColor(alert.level) }}
                      >
                        {point.value}
                      </div>
                      <div className="flex-1 flex flex-column justify-content-between">
                        <div
                          className="text-sm mb-2"
                          style={{ color: 'var(--bq-text-secondary)' }}
                        >
                          <i className="pi pi-info-circle mr-1" />
                          <strong>Threshold:</strong> {point.threshold}
                        </div>
                        <div
                          className="text-sm p-2 border-round"
                          style={{
                            color: 'var(--bq-text-secondary)',
                            borderLeft: `3px solid ${getLevelColor(alert.level)}`,
                          }}
                        >
                          <strong>Concern:</strong> {point.concern}
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Affected Positions */}
        {alert.affected_positions && alert.affected_positions.length > 0 && (
          <div>
            <div className="flex align-items-center gap-2 mb-3">
              <i
                className="pi pi-list"
                style={{
                  color: 'var(--bq-color-info)',
                  fontSize: '1.25rem',
                }}
              />
              <h4
                className="m-0"
                style={{ color: 'var(--bq-text-primary)', fontSize: '1.1rem' }}
              >
                Affected Positions
              </h4>
              <Tag
                value={alert.affected_positions.length}
                severity="info"
                rounded
                style={{
                  borderRadius: '50%',
                  minWidth: '1.75rem',
                  height: '1.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                }}
              />
            </div>
            <Card
              className="grid-stack-item-content"
              pt={{
                root: {
                  style: {
                    backgroundColor: 'var(--bq-surface-body)',
                  },
                },
                body: { className: 'p-3 flex-1' },
                content: { className: 'p-0 m-0 flex-1' },
              }}
            >
              <Tooltip target=".risk-alert-ticker-tag" position="top" />
              <div className="flex flex-wrap gap-2">
                {alert.affected_positions.map((position, index) => {
                  const companyName = getCompanyName(position);
                  return (
                    <div key={index} style={{ position: 'relative' }}>
                      <Tag
                        value={position}
                        severity="secondary"
                        rounded
                        className="risk-alert-ticker-tag"
                        title={companyName || position}
                        data-pr-tooltip={companyName || position}
                        style={{
                          fontSize: '0.9rem',
                          padding: '0.5rem 0.75rem',
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>
        )}

        {/* Recommended Action */}
        {alert.recommended_action && (
          <div>
            <div className="flex align-items-center gap-2 mb-3">
              <i
                className="pi pi-lightbulb"
                style={{
                  color: getLevelColor(alert.level),
                  fontSize: '1.25rem',
                }}
              />
              <h4
                className="m-0"
                style={{
                  color: 'var(--bq-text-primary)',
                  fontSize: '1.1rem',
                }}
              >
                Recommended Action
              </h4>
            </div>
            <Card
              className="grid-stack-item-content"
              pt={{
                root: {
                  style: {
                    backgroundColor: 'var(--bq-surface-body)',
                    border: `2px solid ${getLevelColor(alert.level)}`,
                  },
                },
                body: { className: 'p-3 flex-1' },
                content: { className: 'p-0 m-0 flex-1' },
              }}
            >
              <p
                style={{
                  color: 'var(--bq-text-primary)',
                  margin: 0,
                  fontSize: '0.95rem',
                  fontWeight: 500,
                }}
              >
                {alert.recommended_action}
              </p>
            </Card>
          </div>
        )}
      </div>
    </Dialog>
  );
};
